import { useCallback, useMemo, useState } from "react"
import { Box, Button, HStack, Input, NativeSelect, Spacer, Stack, Table } from "@chakra-ui/react"
import { FaChevronDown, FaChevronRight, FaTimes } from "react-icons/fa"
import { Text } from "@/components"
import {
  CapTableNote,
  CompanyFinancials,
  CompanyFinancialsAdditionalMetrics,
  CompanyFinancialsBalanceSheet,
  CompanyFinancialsCashFlowStatement,
  CompanyFinancialsIncomeStatement,
  FinancialPeriod,
} from "@/types"
import { KeyMetricsSummary } from "./KeyMetricsSummary"

interface Props {
  companyFinancials: CompanyFinancials | null
  capTableNote?: CapTableNote | null
}

type UnitScale = "actuals" | "thousands" | "millions"

type StatementEntry =
  | CompanyFinancialsIncomeStatement
  | CompanyFinancialsBalanceSheet
  | CompanyFinancialsCashFlowStatement
  | CompanyFinancialsAdditionalMetrics

type SectionKey = "incomeStatement" | "balanceSheet" | "cashFlow" | "additionalMetrics"

type EditedData = {
  [sectionKey: string]: {
    [periodId: number]: {
      [fieldKey: string]: string
    }
  }
}

type FieldConfig = {
  key: string
  label: string
  calculated?: boolean
  percentKey?: string
  isPercentRow?: boolean
  isGrowthRow?: boolean
  sourceKey?: string
  isSubsectionHeader?: boolean
  isComputedMetric?: boolean
}

const SECTION_CONFIG: {
  key: SectionKey
  label: string
  dataKey: keyof CompanyFinancials
  fields: FieldConfig[]
}[] = [
  {
    key: "incomeStatement",
    label: "Income Statement",
    dataKey: "companyFinancialsIncomeStatement",
    fields: [
      { key: "projectedArr", label: "Projected ARR" },
      { key: "projectedRevenue", label: "Projected Revenue" },
      { key: "projectedGrossProfit", label: "Projected Gross Profit" },
      { key: "projectedNetIncome", label: "Projected Net Income" },
      { key: "arr", label: "ARR" },
      { key: "bookings", label: "Bookings" },
      { key: "revenue", label: "Revenue" },
      { key: "costOfSales", label: "Cost of Sales", calculated: true, percentKey: "costOfSalesPct" },
      { key: "grossProfit", label: "Gross Profit", percentKey: "grossProfitPct" },
      { key: "totalOpEx", label: "Operating Expenses", percentKey: "totalOpExPct" },
      { key: "operatingIncome", label: "Operating Income", calculated: true, percentKey: "operatingIncomePct" },
      { key: "totalOtherIncomeExpenses", label: "Total Other Income (Expenses)", calculated: true, percentKey: "totalOtherIncomeExpensesPct" },
      { key: "netIncome", label: "Net Income", percentKey: "netIncomePct" },
      { key: "depreciation", label: "Depreciation" },
      { key: "ebitda", label: "EBITDA" },
      { key: "sm", label: "S&M", percentKey: "smPctOpEx" },
      { key: "ga", label: "G&A", calculated: true, percentKey: "gaPctOpEx" },
      { key: "rd", label: "R&D", percentKey: "rdPctOpEx" },
      { key: "smPctRevenue", label: "S&M % of Revenue", calculated: true, isPercentRow: true },
      { key: "gaPctRevenue", label: "G&A % of Revenue", calculated: true, isPercentRow: true },
      { key: "rdPctRevenue", label: "R&D % of Revenue", calculated: true, isPercentRow: true },
      { key: "arrGrowth", label: "ARR Growth", isGrowthRow: true, sourceKey: "arr" },
      { key: "newArrGrowth", label: "New ARR Growth", isGrowthRow: true, sourceKey: "newArr" },
      { key: "revenueGrowth", label: "Revenue Growth", isGrowthRow: true, sourceKey: "revenue" },
      { key: "costOfSalesGrowth", label: "Cost of Sales Growth", isGrowthRow: true, sourceKey: "costOfSales" },
      { key: "grossProfitGrowth", label: "Gross Profit Growth", isGrowthRow: true, sourceKey: "grossProfit" },
      { key: "operatingExpensesGrowth", label: "Operating Expenses Growth", isGrowthRow: true, sourceKey: "totalOpEx" },
      { key: "operatingIncomeGrowth", label: "Operating Income Growth", isGrowthRow: true, sourceKey: "operatingIncome" },
      { key: "totalOtherIncomeGrowth", label: "Total Other Income Growth", isGrowthRow: true, sourceKey: "totalOtherIncomeExpenses" },
      { key: "netIncomeGrowth", label: "Net Income Growth", isGrowthRow: true, sourceKey: "netIncome" },
    ],
  },
  {
    key: "balanceSheet",
    label: "Balance Sheet",
    dataKey: "companyFinancialsBalanceSheet",
    fields: [
      { key: "assets_header", label: "Assets", isSubsectionHeader: true },
      { key: "cash", label: "Cash" },
      { key: "accountsReceivable", label: "Accounts Receivable" },
      { key: "inventories", label: "Inventories" },
      { key: "prepaidExpensesOther", label: "Prepaid Expenses & Other Current Assets", calculated: true },
      { key: "totalCurrentAssets", label: "Total Current Assets" },
      { key: "netFixedAssets", label: "Net Fixed Assets" },
      { key: "goodwillIntangibles", label: "Goodwill & Intangibles" },
      { key: "otherAssets", label: "Other Assets", calculated: true },
      { key: "totalAssets", label: "Total Assets" },
      { key: "liabilities_header", label: "Liabilities", isSubsectionHeader: true },
      { key: "notesPayable", label: "Notes Payable" },
      { key: "accountsPayable", label: "Accounts Payable" },
      { key: "currentPortionLongTermDebt", label: "Current Portion LTD" },
      { key: "accruedExpensesOther", label: "Accrued Expenses & Others", calculated: true },
      { key: "totalCurrentLiabilities", label: "Total Current Liabilities" },
      { key: "longTermDebt", label: "Long Term Debt" },
      { key: "otherLiabilities", label: "Other Liabilities", calculated: true },
      { key: "totalLiabilities", label: "Total Liabilities" },
      { key: "equity_header", label: "Shareholder's Equity", isSubsectionHeader: true },
      { key: "totalLiabilitiesAndEquity", label: "Total Liabilities & Equity", calculated: true },
      { key: "totalEquity", label: "Total Equity", calculated: true },
    ],
  },
  {
    key: "cashFlow",
    label: "Cash Flow",
    dataKey: "companyFinancialsCashFlowStatement",
    fields: [
      { key: "cashFlowOperating", label: "Cash Flow Operating" },
      { key: "cashFlowInvesting", label: "Cash Flow Investing" },
      { key: "cashFlowFinancing", label: "Cash Flow Financing" },
      { key: "netCashIncrease", label: "Net Cash Increase" },
    ],
  },
  {
    key: "additionalMetrics",
    label: "Additional Metrics",
    dataKey: "companyFinancialsAdditionalMetrics",
    fields: [
      { key: "arrChurn", label: "ARR Churn" },
      { key: "arrContraction", label: "ARR Contraction" },
      { key: "arrExpansion", label: "ARR Expansion" },
      { key: "newCustomerArr", label: "New Customer ARR" },
      { key: "netRevenueRetention", label: "Net Revenue Retention" },
      { key: "monthlyBurn", label: "Monthly Burn", calculated: true, isComputedMetric: true },
      { key: "impliedMrr", label: "Implied MRR", calculated: true, isComputedMetric: true },
      { key: "monthsOfRunway", label: "Months of Runway", calculated: true, isComputedMetric: true },
      { key: "revenueMultiple", label: "Revenue Multiple", calculated: true, isComputedMetric: true },
    ],
  },
]

const QUARTER_LABELS = new Set(["q1", "q2", "q3", "q4"])

const isQuarterLabel = (label: string) => QUARTER_LABELS.has(label.toLowerCase())

const formatValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "\u2014"
  const num = Number(value)
  if (isNaN(num)) return String(value)
  if (num < 0) return `(${new Intl.NumberFormat("en-US").format(Math.abs(num))})`
  return new Intl.NumberFormat("en-US").format(num)
}

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) return null
  return `${(value * 100).toFixed(1)}%`
}

const calculateIncomeStatementValues = (entry: CompanyFinancialsIncomeStatement) => {
  const revenue = entry.revenue ? Number(entry.revenue) : null
  const grossProfit = entry.grossProfit ? Number(entry.grossProfit) : null
  const netIncome = entry.netIncome ? Number(entry.netIncome) : null
  const totalOpEx = entry.totalOpEx ? Number(entry.totalOpEx) : null
  const sm = entry.sm ? Number(entry.sm) : null
  const rd = entry.rd ? Number(entry.rd) : null
  const arr = entry.arr ? Number(entry.arr) : null

  const costOfSales = revenue !== null && grossProfit !== null ? revenue - grossProfit : null
  const operatingIncome = grossProfit !== null && totalOpEx !== null ? grossProfit - totalOpEx : null
  const totalOtherIncomeExpenses = netIncome !== null && operatingIncome !== null ? netIncome - operatingIncome : null
  const ga = totalOpEx !== null && rd !== null && sm !== null ? totalOpEx - rd - sm : null

  const costOfSalesPct = revenue && costOfSales !== null ? costOfSales / revenue : null
  const grossProfitPct = revenue && grossProfit !== null ? grossProfit / revenue : null
  const totalOpExPct = revenue && totalOpEx !== null ? totalOpEx / revenue : null
  const operatingIncomePct = revenue && operatingIncome !== null ? operatingIncome / revenue : null
  const totalOtherIncomeExpensesPct = revenue && totalOtherIncomeExpenses !== null ? totalOtherIncomeExpenses / revenue : null
  const netIncomePct = revenue && netIncome !== null ? netIncome / revenue : null

  const smPctOpEx = totalOpEx && sm !== null ? sm / totalOpEx : null
  const gaPctOpEx = totalOpEx && ga !== null ? ga / totalOpEx : null
  const rdPctOpEx = totalOpEx && rd !== null ? rd / totalOpEx : null

  const revenueOrArr = revenue || arr
  const smPctRevenue = revenueOrArr && sm !== null ? sm / revenueOrArr : null
  const gaPctRevenue = revenueOrArr && ga !== null ? ga / revenueOrArr : null
  const rdPctRevenue = revenueOrArr && rd !== null ? rd / revenueOrArr : null

  return {
    costOfSales, operatingIncome, totalOtherIncomeExpenses, ga,
    costOfSalesPct, grossProfitPct, totalOpExPct, operatingIncomePct,
    totalOtherIncomeExpensesPct, netIncomePct,
    smPctOpEx, gaPctOpEx, rdPctOpEx,
    smPctRevenue, gaPctRevenue, rdPctRevenue,
  }
}

const calculateBalanceSheetValues = (entry: CompanyFinancialsBalanceSheet) => {
  const cash = entry.cash ? Number(entry.cash) : null
  const accountsReceivable = entry.accountsReceivable ? Number(entry.accountsReceivable) : null
  const inventories = entry.inventories ? Number(entry.inventories) : null
  const totalCurrentAssets = entry.totalCurrentAssets ? Number(entry.totalCurrentAssets) : null
  const netFixedAssets = entry.netFixedAssets ? Number(entry.netFixedAssets) : null
  const goodwillIntangibles = entry.goodwillIntangibles ? Number(entry.goodwillIntangibles) : null
  const totalAssets = entry.totalAssets ? Number(entry.totalAssets) : null
  const notesPayable = entry.notesPayable ? Number(entry.notesPayable) : null
  const accountsPayable = entry.accountsPayable ? Number(entry.accountsPayable) : null
  const currentPortionLongTermDebt = entry.currentPortionLongTermDebt ? Number(entry.currentPortionLongTermDebt) : null
  const totalCurrentLiabilities = entry.totalCurrentLiabilities ? Number(entry.totalCurrentLiabilities) : null
  const longTermDebt = entry.longTermDebt ? Number(entry.longTermDebt) : null
  const totalLiabilities = entry.totalLiabilities ? Number(entry.totalLiabilities) : null

  const prepaidExpensesOther =
    totalCurrentAssets !== null && cash !== null && accountsReceivable !== null && inventories !== null
      ? totalCurrentAssets - (cash + accountsReceivable + inventories) : null

  const otherAssets =
    totalAssets !== null && totalCurrentAssets !== null && netFixedAssets !== null && goodwillIntangibles !== null
      ? totalAssets - (totalCurrentAssets + netFixedAssets + goodwillIntangibles) : null

  const accruedExpensesOther =
    totalCurrentLiabilities !== null && notesPayable !== null && accountsPayable !== null && currentPortionLongTermDebt !== null
      ? totalCurrentLiabilities - (notesPayable + accountsPayable + currentPortionLongTermDebt) : null

  const otherLiabilities =
    totalLiabilities !== null && totalCurrentLiabilities !== null && longTermDebt !== null
      ? totalLiabilities - totalCurrentLiabilities - longTermDebt : null

  const totalLiabilitiesAndEquity = totalAssets
  const totalEquity =
    totalLiabilitiesAndEquity !== null && totalLiabilities !== null
      ? totalLiabilitiesAndEquity - totalLiabilities : null

  return { prepaidExpensesOther, otherAssets, accruedExpensesOther, otherLiabilities, totalLiabilitiesAndEquity, totalEquity }
}

const calculateMonthlyBurn = (cashFlowOperating: number | null, netIncome: number | null): number | string | null => {
  if (cashFlowOperating === null) {
    if (netIncome === null) return null
    if (netIncome > 0) return "Profitable"
    return netIncome / 12
  }
  if (cashFlowOperating > 0) return "CF from Op Positive"
  return cashFlowOperating / 12
}

const calculateImpliedMrr = (arr: number | null, revenue: number | null): number | null => {
  if (arr) return arr / 12
  if (revenue) return revenue / 12
  return null
}

const calculateMonthsOfRunway = (cash: number | null, monthlyBurn: number | string | null): number | string | null => {
  if (cash === null || monthlyBurn === null) return null
  if (typeof monthlyBurn === "string") return "N/A"
  if (monthlyBurn >= 0) return "N/A"
  return Math.round((-1 * cash) / monthlyBurn)
}

const calculateRevenueMultiple = (
  companyFDValuation: number | null, revenue: number | null, unit: UnitScale
): number | string | null => {
  if (companyFDValuation === null || revenue === null || revenue === 0) return null
  const scaleMap: Record<UnitScale, number> = { actuals: 1, thousands: 1_000, millions: 1_000_000 }
  return (companyFDValuation / scaleMap[unit]) / revenue
}

const calculateCompanyFDValuation = (capTableNote: CapTableNote | null | undefined): number | null => {
  if (!capTableNote) return null
  const totalAuthShares = Number(capTableNote.totalAuthShares)
  const fmv = Number(capTableNote.fmv)
  const esoLrp = Number(capTableNote.esoLrp)
  if (isNaN(totalAuthShares) || (isNaN(fmv) && isNaN(esoLrp))) return null
  return totalAuthShares * Math.max(fmv || 0, esoLrp || 0)
}

const getPeriod = (entry: StatementEntry): FinancialPeriod => entry.companyFinancialPeriod

export const CompanyFinancialsView = ({ companyFinancials, capTableNote }: Props) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<SectionKey>>(new Set())
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(new Set())
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [hideEmptyRows, setHideEmptyRows] = useState(false)
  const [yearsInitialized, setYearsInitialized] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState<EditedData>({})
  const [originalData, setOriginalData] = useState<EditedData | null>(null)
  const [yearRangeStart, setYearRangeStart] = useState<number | null>(null)
  const [yearRangeEnd, setYearRangeEnd] = useState<number | null>(null)

  const startEditing = () => {
    if (!companyFinancials) return
    const initialData: EditedData = {}
    SECTION_CONFIG.forEach((section) => {
      const entries = companyFinancials[section.dataKey] as StatementEntry[]
      initialData[section.key] = {}
      entries.forEach((entry) => {
        const periodId = getPeriod(entry).id
        initialData[section.key][periodId] = {}
        section.fields.forEach((field) => {
          if (!field.calculated && !field.isPercentRow && !field.isGrowthRow) {
            const value = (entry as Record<string, unknown>)[field.key]
            initialData[section.key][periodId][field.key] = value !== null && value !== undefined ? String(value) : ""
          }
        })
      })
    })
    setOriginalData(JSON.parse(JSON.stringify(initialData)))
    setEditedData(initialData)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    if (originalData) setEditedData(JSON.parse(JSON.stringify(originalData)))
    setIsEditing(false)
    setOriginalData(null)
  }

  const saveEditing = () => {
    // TODO: Call API to save editedData
    console.log("Saving edited data:", editedData)
    setIsEditing(false)
    setOriginalData(null)
  }

  const handleEditValue = (sectionKey: string, periodId: number, fieldKey: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [periodId]: { ...prev[sectionKey]?.[periodId], [fieldKey]: value },
      },
    }))
  }

  const allPeriods = useMemo(() => {
    if (!companyFinancials) return []
    const periodMap = new Map<number, FinancialPeriod>()
    const collectPeriods = (entries: StatementEntry[]) => {
      entries.forEach((entry) => {
        const p = getPeriod(entry)
        if (!periodMap.has(p.id)) periodMap.set(p.id, p)
      })
    }
    collectPeriods(companyFinancials.companyFinancialsIncomeStatement)
    collectPeriods(companyFinancials.companyFinancialsBalanceSheet)
    collectPeriods(companyFinancials.companyFinancialsCashFlowStatement)
    collectPeriods(companyFinancials.companyFinancialsAdditionalMetrics)
    return Array.from(periodMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year
      const order = ["q1", "q2", "q3", "q4"]
      const aIdx = order.indexOf(a.label.toLowerCase())
      const bIdx = order.indexOf(b.label.toLowerCase())
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
      return a.label.localeCompare(b.label)
    })
  }, [companyFinancials])

  const yearGroups = useMemo(() => {
    const groups = new Map<number, FinancialPeriod[]>()
    allPeriods.forEach((period) => {
      if (!groups.has(period.year)) groups.set(period.year, [])
      groups.get(period.year)!.push(period)
    })
    return groups
  }, [allPeriods])

  const availableYears = useMemo(() => Array.from(yearGroups.keys()).sort((a, b) => a - b), [yearGroups])

  useMemo(() => {
    if (yearsInitialized || yearGroups.size === 0) return
    const quartersToCollapse = new Set<number>()
    yearGroups.forEach((periods, year) => {
      if (periods.some((p) => isQuarterLabel(p.label))) quartersToCollapse.add(year)
    })
    setCollapsedYears(quartersToCollapse)
    setYearsInitialized(true)
  }, [yearGroups, yearsInitialized])

  const buildLookup = (entries: StatementEntry[]) => {
    const map = new Map<number, StatementEntry>()
    entries.forEach((entry) => {
      if (showActiveOnly && !entry.isActive) return
      map.set(getPeriod(entry).id, entry)
    })
    return map
  }

  const visiblePeriods = useMemo(() => {
    let periods = allPeriods
    if (showActiveOnly && companyFinancials) {
      const activePeriodIds = new Set<number>()
      const allEntries: StatementEntry[] = [
        ...companyFinancials.companyFinancialsIncomeStatement,
        ...companyFinancials.companyFinancialsBalanceSheet,
        ...companyFinancials.companyFinancialsCashFlowStatement,
        ...companyFinancials.companyFinancialsAdditionalMetrics,
      ]
      allEntries.forEach((entry) => {
        if (entry.isActive) activePeriodIds.add(getPeriod(entry).id)
      })
      periods = periods.filter((p) => activePeriodIds.has(p.id))
    }
    if (yearRangeStart !== null || yearRangeEnd !== null) {
      periods = periods.filter((period) => {
        const startOk = yearRangeStart === null || period.year >= yearRangeStart
        const endOk = yearRangeEnd === null || period.year <= yearRangeEnd
        return startOk && endOk
      })
    }
    return periods.filter((period) => {
      if (isQuarterLabel(period.label) && collapsedYears.has(period.year)) return false
      return true
    })
  }, [allPeriods, collapsedYears, showActiveOnly, companyFinancials, yearRangeStart, yearRangeEnd])

  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleYear = (year: number) => {
    setCollapsedYears((prev) => {
      const next = new Set(prev)
      if (next.has(year)) next.delete(year)
      else next.add(year)
      return next
    })
  }

  if (!companyFinancials) {
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>Company Financials</Text>
        <Text color="text.muted">No financial data found.</Text>
      </Box>
    )
  }

  const yearHeaderCells: React.ReactNode[] = []
  const periodHeaderCells: React.ReactNode[] = []
  const sortedYears = Array.from(yearGroups.keys()).sort((a, b) => a - b)

  sortedYears.forEach((year) => {
    const periods = yearGroups.get(year)!
    const hasQuarters = periods.some((p) => isQuarterLabel(p.label))
    const isCollapsed = collapsedYears.has(year)
    const yearVisiblePeriods = visiblePeriods.filter((p) => p.year === year)
    if (yearVisiblePeriods.length === 0) return

    yearHeaderCells.push(
      <Table.ColumnHeader
        key={year} colSpan={yearVisiblePeriods.length} textAlign="center"
        cursor={hasQuarters ? "pointer" : "default"}
        onClick={hasQuarters ? () => toggleYear(year) : undefined}
        _hover={hasQuarters ? { bg: "table.section" } : undefined}
        bg="table.header"
      >
        <HStack justifyContent="center" gap={1}>
          {hasQuarters && (isCollapsed ? <FaChevronRight size={10} /> : <FaChevronDown size={10} />)}
          <Text fontSize="xs" fontWeight="bold">{year}</Text>
        </HStack>
      </Table.ColumnHeader>
    )

    yearVisiblePeriods.forEach((period) => {
      periodHeaderCells.push(
        <Table.ColumnHeader key={period.id} textAlign="center" minW="100px">
          <Text fontSize="xs">{period.label.toUpperCase()}</Text>
        </Table.ColumnHeader>
      )
    })
  })

  return (
    <Stack gap={4}>
      <KeyMetricsSummary companyFinancials={companyFinancials} capTableNote={capTableNote} />

      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>Company Financials</Text>

        <HStack mb={4} gap={4} flexWrap="wrap">
          <Text fontSize="xs" color="text.muted">
            Fiscal Year End: <strong>{companyFinancials.monthFiscalYearEnd}</strong>
          </Text>
          <Text fontSize="xs" color="text.muted">
            Presented In: <strong>{companyFinancials.financialsPresentedIn}</strong>
          </Text>
          <Text fontSize="xs" color="text.muted">
            Currency: <strong>{companyFinancials.currency?.toUpperCase()}</strong>
          </Text>
          {companyFinancials.reviewedBy && (
            <Text fontSize="xs" color="text.muted">
              Reviewed By: <strong>{companyFinancials.reviewedBy}</strong>
            </Text>
          )}
        </HStack>

        <HStack mb={4} gap={2} flexWrap="wrap">
          <Button size="xs" variant={showActiveOnly ? "solid" : "outline"} borderColor="border.default"
            onClick={() => setShowActiveOnly(!showActiveOnly)}>
            {showActiveOnly ? "Hide Empty Columns" : "Hiding Empty Columns"}
          </Button>
          <Button size="xs" variant={hideEmptyRows ? "solid" : "outline"} borderColor="border.default"
            onClick={() => setHideEmptyRows(!hideEmptyRows)}>
            {hideEmptyRows ? "Hiding Empty Rows" : "Hide Empty Rows"}
          </Button>
          <HStack gap={1}>
            <Text fontSize="xs" color="text.muted">Years:</Text>
            <NativeSelect.Root size="xs" w="80px">
              <NativeSelect.Field value={yearRangeStart ?? ""} onChange={(e) => {
                const val = e.target.value === "" ? null : Number(e.target.value)
                setYearRangeStart(val)
                if (val !== null && yearRangeEnd !== null && yearRangeEnd < val) setYearRangeEnd(null)
              }}>
                <option value="">All</option>
                {availableYears.map((year) => <option key={year} value={year}>{year}</option>)}
              </NativeSelect.Field>
            </NativeSelect.Root>
            <Text fontSize="xs" color="text.muted">to</Text>
            <NativeSelect.Root size="xs" w="80px">
              <NativeSelect.Field value={yearRangeEnd ?? ""} onChange={(e) => {
                const val = e.target.value === "" ? null : Number(e.target.value)
                setYearRangeEnd(val)
                if (val !== null && yearRangeStart !== null && yearRangeStart > val) setYearRangeStart(null)
              }}>
                <option value="">All</option>
                {availableYears.map((year) => <option key={year} value={year}>{year}</option>)}
              </NativeSelect.Field>
            </NativeSelect.Root>
            {(yearRangeStart !== null || yearRangeEnd !== null) && (
              <Button size="xs" variant="ghost" onClick={() => { setYearRangeStart(null); setYearRangeEnd(null) }}>
                <FaTimes />
              </Button>
            )}
          </HStack>
          <Spacer />
          {isEditing ? (
            <HStack gap={2}>
              <Button size="xs" variant="outline" borderColor="border.default" onClick={cancelEditing}><FaTimes /> Cancel</Button>
              <Button size="xs" variant="solid" onClick={saveEditing}>Save</Button>
            </HStack>
          ) : (
            <Button size="xs" variant="outline" borderColor="border.default" onClick={startEditing}>Edit</Button>
          )}
        </HStack>

        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" borderColor="table.border" css={{ "& td, & th": { borderColor: "table.border" } }}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="200px" bg="table.header" position="sticky" left={0} zIndex={1} />
                {yearHeaderCells}
              </Table.Row>
              <Table.Row>
                <Table.ColumnHeader w="200px" bg="table.header" position="sticky" left={0} zIndex={1}>
                  <Text fontSize="xs" fontWeight="bold">Metric</Text>
                </Table.ColumnHeader>
                {periodHeaderCells}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {SECTION_CONFIG.map((section) => {
                const isSectionCollapsed = collapsedSections.has(section.key)
                const entries = companyFinancials[section.dataKey] as StatementEntry[]
                const lookup = buildLookup(entries)
                const incomeStatementLookup = buildLookup(companyFinancials.companyFinancialsIncomeStatement)
                const balanceSheetLookup = buildLookup(companyFinancials.companyFinancialsBalanceSheet)
                const cashFlowLookup = buildLookup(companyFinancials.companyFinancialsCashFlowStatement)

                return (
                  <SectionRows
                    key={section.key}
                    section={section}
                    isCollapsed={isSectionCollapsed}
                    onToggle={() => toggleSection(section.key)}
                    visiblePeriods={visiblePeriods}
                    lookup={lookup}
                    allEntries={entries}
                    hideEmptyRows={hideEmptyRows}
                    isEditing={isEditing}
                    editedData={editedData[section.key] || {}}
                    onEditValue={(periodId, fieldKey, value) => handleEditValue(section.key, periodId, fieldKey, value)}
                    incomeStatementLookup={incomeStatementLookup}
                    balanceSheetLookup={balanceSheetLookup}
                    cashFlowLookup={cashFlowLookup}
                    capTableNote={capTableNote}
                    financialsPresentedIn={companyFinancials.financialsPresentedIn}
                  />
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>

        {companyFinancials.notes && (
          <Box mt={4} p={3} bg="notes.bg" borderRadius="md">
            <Text fontSize="xs" color="text.muted" fontWeight="medium" mb={1}>Notes</Text>
            <Text fontSize="sm">{companyFinancials.notes}</Text>
          </Box>
        )}
      </Box>
    </Stack>
  )
}

const SectionRows = ({
  section, isCollapsed, onToggle, visiblePeriods, lookup, allEntries, hideEmptyRows,
  isEditing, editedData, onEditValue,
  incomeStatementLookup, balanceSheetLookup, cashFlowLookup, capTableNote, financialsPresentedIn,
}: {
  section: (typeof SECTION_CONFIG)[number]
  isCollapsed: boolean
  onToggle: () => void
  visiblePeriods: FinancialPeriod[]
  lookup: Map<number, StatementEntry>
  allEntries: StatementEntry[]
  hideEmptyRows: boolean
  isEditing: boolean
  editedData: { [periodId: number]: { [fieldKey: string]: string } }
  onEditValue: (periodId: number, fieldKey: string, value: string) => void
  incomeStatementLookup: Map<number, StatementEntry>
  balanceSheetLookup: Map<number, StatementEntry>
  cashFlowLookup: Map<number, StatementEntry>
  capTableNote?: CapTableNote | null
  financialsPresentedIn?: string | null
}) => {
  const calculatedIncomeValues = useMemo(() => {
    if (section.key !== "incomeStatement") return new Map()
    const map = new Map<number, ReturnType<typeof calculateIncomeStatementValues>>()
    visiblePeriods.forEach((period) => {
      const entry = lookup.get(period.id) as CompanyFinancialsIncomeStatement | undefined
      if (entry) map.set(period.id, calculateIncomeStatementValues(entry))
    })
    return map
  }, [section.key, visiblePeriods, lookup])

  const calculatedBalanceSheetValues = useMemo(() => {
    if (section.key !== "balanceSheet") return new Map()
    const map = new Map<number, ReturnType<typeof calculateBalanceSheetValues>>()
    visiblePeriods.forEach((period) => {
      const entry = lookup.get(period.id) as CompanyFinancialsBalanceSheet | undefined
      if (entry) map.set(period.id, calculateBalanceSheetValues(entry))
    })
    return map
  }, [section.key, visiblePeriods, lookup])

  const computedMetricsValues = useMemo(() => {
    if (section.key !== "additionalMetrics") return new Map()
    const companyFDValuation = calculateCompanyFDValuation(capTableNote)
    const unitScale: UnitScale = financialsPresentedIn?.toLowerCase().includes("thousand")
      ? "thousands" : financialsPresentedIn?.toLowerCase().includes("million") ? "millions" : "actuals"

    const map = new Map<number, { monthlyBurn: number | string | null; impliedMrr: number | null; monthsOfRunway: number | string | null; revenueMultiple: number | string | null }>()
    visiblePeriods.forEach((period) => {
      const incomeEntry = incomeStatementLookup.get(period.id) as CompanyFinancialsIncomeStatement | undefined
      const balanceEntry = balanceSheetLookup.get(period.id) as CompanyFinancialsBalanceSheet | undefined
      const cashFlowEntry = cashFlowLookup.get(period.id) as CompanyFinancialsCashFlowStatement | undefined

      const arr = incomeEntry?.arr ? Number(incomeEntry.arr) : null
      const revenue = incomeEntry?.revenue ? Number(incomeEntry.revenue) : null
      const netIncome = incomeEntry?.netIncome ? Number(incomeEntry.netIncome) : null
      const cash = balanceEntry?.cash ? Number(balanceEntry.cash) : null
      const cashFlowOperating = cashFlowEntry?.cashFlowOperating ? Number(cashFlowEntry.cashFlowOperating) : null

      const monthlyBurn = calculateMonthlyBurn(cashFlowOperating, netIncome)
      const impliedMrr = calculateImpliedMrr(arr, revenue)
      const monthsOfRunway = calculateMonthsOfRunway(cash, monthlyBurn)
      const revenueMultiple = calculateRevenueMultiple(companyFDValuation, revenue, unitScale)
      map.set(period.id, { monthlyBurn, impliedMrr, monthsOfRunway, revenueMultiple })
    })
    return map
  }, [section.key, visiblePeriods, incomeStatementLookup, balanceSheetLookup, cashFlowLookup, capTableNote, financialsPresentedIn])

  const periodByLabelYear = useMemo(() => {
    const map = new Map<string, StatementEntry>()
    allEntries.forEach((entry) => {
      const p = getPeriod(entry)
      map.set(`${p.label.toLowerCase()}-${p.year}`, entry)
    })
    return map
  }, [allEntries])

  const getGrowthValue = useCallback((field: FieldConfig, period: FinancialPeriod): number | null => {
    if (!field.isGrowthRow || !field.sourceKey) return null
    const label = period.label.toLowerCase()
    if (label !== "fy" && !QUARTER_LABELS.has(label)) return null

    const currentEntry = periodByLabelYear.get(`${label}-${period.year}`)
    const previousEntry = periodByLabelYear.get(`${label}-${period.year - 1}`)
    if (!currentEntry || !previousEntry) return null

    const calculatedFields = ["costOfSales", "operatingIncome", "totalOtherIncomeExpenses", "ga"]
    let currentValue: number | null = null
    let previousValue: number | null = null

    if (calculatedFields.includes(field.sourceKey)) {
      const currentCalc = calculateIncomeStatementValues(currentEntry as CompanyFinancialsIncomeStatement)
      const previousCalc = calculateIncomeStatementValues(previousEntry as CompanyFinancialsIncomeStatement)
      currentValue = currentCalc[field.sourceKey as keyof typeof currentCalc] as number | null
      previousValue = previousCalc[field.sourceKey as keyof typeof previousCalc] as number | null
    } else {
      const currentRaw = (currentEntry as Record<string, unknown>)[field.sourceKey]
      const previousRaw = (previousEntry as Record<string, unknown>)[field.sourceKey]
      currentValue = currentRaw ? Number(currentRaw) : null
      previousValue = previousRaw ? Number(previousRaw) : null
    }

    if (currentValue === null || previousValue === null) return null
    return currentValue - previousValue
  }, [periodByLabelYear])

  const getFieldValue = (field: FieldConfig, entry: StatementEntry | undefined, periodId: number): string | number | null => {
    if (field.isComputedMetric && section.key === "additionalMetrics") {
      const metrics = computedMetricsValues.get(periodId)
      if (!metrics) return null
      return metrics[field.key as keyof typeof metrics]
    }
    if (!entry) return null
    if (field.calculated && section.key === "incomeStatement") {
      const calc = calculatedIncomeValues.get(periodId)
      if (!calc) return null
      return calc[field.key as keyof typeof calc] as number | null
    }
    if (field.calculated && section.key === "balanceSheet") {
      const calc = calculatedBalanceSheetValues.get(periodId)
      if (!calc) return null
      return calc[field.key as keyof typeof calc] as number | null
    }
    return (entry as Record<string, unknown>)[field.key] as string | null
  }

  const getPercentValue = (field: FieldConfig, periodId: number): string | null => {
    if (!field.percentKey || section.key !== "incomeStatement") return null
    const calc = calculatedIncomeValues.get(periodId)
    if (!calc) return null
    return formatPercent(calc[field.percentKey as keyof typeof calc] as number | null)
  }

  const visibleFields = useMemo(() => {
    if (!hideEmptyRows) return section.fields

    const fieldHasData = (field: FieldConfig): boolean => {
      if (field.isSubsectionHeader) return true
      return visiblePeriods.some((period) => {
        const entry = lookup.get(period.id)
        if (field.isPercentRow) {
          const calc = calculatedIncomeValues.get(period.id)
          const pct = calc ? calc[field.key as keyof typeof calc] as number | null : null
          return pct !== null && !isNaN(pct) && isFinite(pct)
        }
        if (field.isGrowthRow) return getGrowthValue(field, period) !== null
        if (field.isComputedMetric && section.key === "additionalMetrics") {
          const metrics = computedMetricsValues.get(period.id)
          if (!metrics) return false
          const value = metrics[field.key as keyof typeof metrics]
          return value !== null && value !== undefined && value !== "N/A"
        }
        if (field.calculated && section.key === "incomeStatement") {
          const calc = calculatedIncomeValues.get(period.id)
          if (!calc) return false
          return (calc[field.key as keyof typeof calc] as number | null) !== null
        }
        if (field.calculated && section.key === "balanceSheet") {
          const calc = calculatedBalanceSheetValues.get(period.id)
          if (!calc) return false
          return (calc[field.key as keyof typeof calc] as number | null) !== null
        }
        const value = entry ? (entry as Record<string, unknown>)[field.key] as string | null : null
        return value !== null && value !== undefined && value !== "" && value !== "0"
      })
    }

    return section.fields.filter(fieldHasData)
  }, [hideEmptyRows, section.fields, section.key, visiblePeriods, lookup, calculatedIncomeValues, calculatedBalanceSheetValues, computedMetricsValues, getGrowthValue])

  return (
    <>
      <Table.Row>
        <Table.Cell bg="table.section" position="sticky" left={0} zIndex={1} cursor="pointer" onClick={onToggle} _hover={{ bg: "table.header" }}>
          <HStack gap={1}>
            {isCollapsed ? <FaChevronRight size={10} /> : <FaChevronDown size={10} />}
            <Text fontSize="xs" fontWeight="bold">{section.label}</Text>
          </HStack>
        </Table.Cell>
        {visiblePeriods.map((period) => <Table.Cell key={period.id} bg="table.section" />)}
      </Table.Row>

      {!isCollapsed && visibleFields.map((field) => {
        if (field.isSubsectionHeader) {
          return (
            <Table.Row key={field.key}>
              <Table.Cell position="sticky" left={0} zIndex={1} bg="table.header" pl={4}>
                <Text fontSize="xs" fontWeight="bold" fontStyle="italic">{field.label}</Text>
              </Table.Cell>
              {visiblePeriods.map((period) => <Table.Cell key={period.id} bg="table.header" />)}
            </Table.Row>
          )
        }

        return (
          <Table.Row key={field.key}>
            <Table.Cell position="sticky" left={0} zIndex={1} bg="card.bg" pl={6}>
              <Text fontSize="xs" whiteSpace="nowrap">{field.label}</Text>
            </Table.Cell>
            {visiblePeriods.map((period) => {
              const entry = lookup.get(period.id)
              const value = getFieldValue(field, entry, period.id)
              const pctValue = getPercentValue(field, period.id)

              if (field.isPercentRow) {
                const calc = calculatedIncomeValues.get(period.id)
                const pct = calc ? calc[field.key as keyof typeof calc] as number | null : null
                return (
                  <Table.Cell key={period.id} textAlign="right">
                    <Text fontSize="xs" whiteSpace="nowrap">{formatPercent(pct) || "\u2014"}</Text>
                  </Table.Cell>
                )
              }

              if (field.isGrowthRow) {
                return (
                  <Table.Cell key={period.id} textAlign="right">
                    <Text fontSize="xs" whiteSpace="nowrap">{formatValue(getGrowthValue(field, period))}</Text>
                  </Table.Cell>
                )
              }

              if (field.isComputedMetric) {
                let displayText = "\u2014"
                if (value !== null && value !== undefined) {
                  if (typeof value === "string") displayText = value
                  else if (field.key === "revenueMultiple") displayText = `${(value as number).toFixed(1)}x`
                  else if (field.key === "monthsOfRunway") displayText = `${Math.round(value as number)}`
                  else displayText = formatValue(Math.round(value as number))
                }
                return (
                  <Table.Cell key={period.id} textAlign="right">
                    <Text fontSize="xs" whiteSpace="nowrap">{displayText}</Text>
                  </Table.Cell>
                )
              }

              if (field.calculated) {
                return (
                  <Table.Cell key={period.id} textAlign="right">
                    <Stack gap={0}>
                      <Text fontSize="xs" whiteSpace="nowrap">{formatValue(value)}</Text>
                      {pctValue && <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">{pctValue}</Text>}
                    </Stack>
                  </Table.Cell>
                )
              }

              const editedValue = editedData[period.id]?.[field.key]
              return (
                <Table.Cell key={period.id} textAlign="right">
                  {isEditing ? (
                    <Input size="xs" value={editedValue ?? ""} onChange={(e) => onEditValue(period.id, field.key, e.target.value)}
                      textAlign="right" w="80px" p={1} fontSize="xs" />
                  ) : (
                    <Stack gap={0}>
                      <Text fontSize="xs" whiteSpace="nowrap">{formatValue(isEditing && editedValue !== undefined ? editedValue : value)}</Text>
                      {pctValue && <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">{pctValue}</Text>}
                    </Stack>
                  )}
                </Table.Cell>
              )
            })}
          </Table.Row>
        )
      })}
    </>
  )
}
